import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Target, 
  Plus, 
  Leaf,
  Users,
  Building2,
  TrendingUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  ArrowRight,
  Trash2,
  CalendarPlus,
  ExternalLink,
  RefreshCw,
  Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUARTERS = [
  "Q1-2026", "Q2-2026", "Q3-2026", "Q4-2026",
  "Q1-2027", "Q2-2027", "Q3-2027", "Q4-2027"
];

const CSRD_INDICATORS = [
  { value: "ESRS E1", label: "ESRS E1 - Changement climatique" },
  { value: "ESRS E2", label: "ESRS E2 - Pollution" },
  { value: "ESRS E3", label: "ESRS E3 - Eau et ressources marines" },
  { value: "ESRS E4", label: "ESRS E4 - Biodiversité" },
  { value: "ESRS E5", label: "ESRS E5 - Économie circulaire" },
  { value: "ESRS S1", label: "ESRS S1 - Effectifs propres" },
  { value: "ESRS S2", label: "ESRS S2 - Travailleurs chaîne de valeur" },
  { value: "ESRS S3", label: "ESRS S3 - Communautés affectées" },
  { value: "ESRS S4", label: "ESRS S4 - Consommateurs" },
  { value: "ESRS G1", label: "ESRS G1 - Conduite des affaires" },
];

export default function Objectives() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [objectives, setObjectives] = useState([]);
  const [orgProgress, setOrgProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    target_value: 75,
    quarter: "",
    description: "",
    csrd_indicator: ""
  });

  const fetchCalendarStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus(response.data);
    } catch (error) {
      console.error("Error fetching calendar status:", error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      const [objectivesRes, progressRes] = await Promise.all([
        axios.get(`${API}/objectives`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/objectives/organization/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setObjectives(objectivesRes.data);
      setOrgProgress(progressRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des objectifs");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchCalendarStatus();
    
    // Check if redirected from calendar connection
    if (searchParams.get('calendar_connected') === 'true') {
      toast.success("Google Calendar connecté avec succès !");
    }
  }, [searchParams, fetchData, fetchCalendarStatus]);

  const handleConnectCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error("Erreur: Google Calendar n'est pas configuré");
    }
  };

  const handleSyncCalendar = async () => {
    setSyncingCalendar(true);
    try {
      const response = await axios.post(`${API}/calendar/sync-objectives`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncingCalendar(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      await axios.delete(`${API}/calendar/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus({ ...calendarStatus, connected: false });
      toast.success("Google Calendar déconnecté");
    } catch (error) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/objectives`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Objectif RSE créé avec succès !");
      setDialogOpen(false);
      setFormData({
        name: "",
        category: "",
        target_value: 75,
        quarter: "",
        description: "",
        csrd_indicator: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/objectives/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Objectif supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/objectives/organization/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `objectifs_rse_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export Excel");
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`${API}/objectives/organization/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `objectifs_rse_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: "Atteint", className: "bg-chart-1 text-white" },
      on_track: { label: "En bonne voie", className: "bg-chart-2 text-white" },
      active: { label: "En cours", className: "bg-chart-5 text-white" },
      at_risk: { label: "À risque", className: "bg-chart-4 text-white" },
      behind: { label: "En retard", className: "bg-destructive text-white" }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "environnement": return <Leaf className="w-5 h-5 text-chart-1" />;
      case "social": return <Users className="w-5 h-5 text-chart-3" />;
      case "gouvernance": return <Building2 className="w-5 h-5 text-chart-5" />;
      default: return <Target className="w-5 h-5 text-primary" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="objectives-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Objectifs RSE
            </h1>
            <p className="text-muted-foreground mt-1">
              Définissez et suivez vos cibles ESG pour la conformité CSRD 2027
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Export buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              data-testid="export-objectives-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              data-testid="export-objectives-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            {/* Google Calendar Integration */}
            {calendarStatus && (
              calendarStatus.connected ? (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSyncCalendar}
                    disabled={syncingCalendar}
                    data-testid="sync-calendar-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingCalendar ? 'animate-spin' : ''}`} />
                    Synchroniser
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleDisconnectCalendar}
                    className="text-muted-foreground"
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Connecté
                  </Button>
                </div>
              ) : calendarStatus.configured ? (
                <Button 
                  variant="outline"
                  onClick={handleConnectCalendar}
                  data-testid="connect-calendar-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Connecter Google Calendar
                </Button>
              ) : null
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full px-6" data-testid="new-objective-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel objectif
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Créer un objectif RSE</DialogTitle>
                <DialogDescription>
                  Définissez une cible ESG alignée avec les exigences CSRD
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de l'objectif</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Réduire les émissions GES de 30%"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                    data-testid="objective-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie ESG</Label>
                    <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                      <SelectTrigger data-testid="objective-category-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="environnement">Environnement</SelectItem>
                        <SelectItem value="social">Social</SelectItem>
                        <SelectItem value="gouvernance">Gouvernance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Trimestre cible</Label>
                    <Select value={formData.quarter} onValueChange={(v) => handleChange("quarter", v)}>
                      <SelectTrigger data-testid="objective-quarter-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {QUARTERS.map(q => (
                          <SelectItem key={q} value={q}>{q}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target">Score cible (%)</Label>
                  <Input
                    id="target"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.target_value}
                    onChange={(e) => handleChange("target_value", parseFloat(e.target.value))}
                    required
                    data-testid="objective-target-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Indicateur CSRD</Label>
                  <Select value={formData.csrd_indicator} onValueChange={(v) => handleChange("csrd_indicator", v)}>
                    <SelectTrigger data-testid="objective-csrd-select">
                      <SelectValue placeholder="Sélectionner un indicateur ESRS" />
                    </SelectTrigger>
                    <SelectContent>
                      {CSRD_INDICATORS.map(ind => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnel)</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez l'objectif et les actions prévues..."
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    rows={3}
                    data-testid="objective-description-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full" data-testid="submit-objective-btn">
                    Créer l'objectif
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Organization Progress Overview */}
        {orgProgress && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="stat-card card-hover border-2" style={{ borderColor: 'hsl(var(--chart-1))' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-chart-1/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-chart-1" />
                  </div>
                  <Badge variant="outline">Global</Badge>
                </div>
                <div className="stat-value text-chart-1" data-testid="overall-progress">
                  {orgProgress.overall_progress}%
                </div>
                <p className="text-sm text-muted-foreground mt-1">Score ESG moyen</p>
              </CardContent>
            </Card>

            <Card className="stat-card card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <Badge variant="outline">CSRD</Badge>
                </div>
                <div className="stat-value text-primary" data-testid="csrd-readiness">
                  {orgProgress.csrd_readiness}%
                </div>
                <p className="text-sm text-muted-foreground mt-1">Conformité CSRD</p>
              </CardContent>
            </Card>

            <Card className="stat-card card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-chart-3/10 flex items-center justify-center">
                    <Target className="w-6 h-6 text-chart-3" />
                  </div>
                  <Badge variant="outline">Objectifs</Badge>
                </div>
                <div className="stat-value text-chart-3">
                  {orgProgress.objectives_summary.completed}/{orgProgress.objectives_summary.total}
                </div>
                <p className="text-sm text-muted-foreground mt-1">Objectifs atteints</p>
              </CardContent>
            </Card>

            <Card className="stat-card card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-chart-4/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-chart-4" />
                  </div>
                  <Badge variant="outline">2027</Badge>
                </div>
                <div className="stat-value text-chart-4">
                  {orgProgress.timeline_to_2027.quarters_remaining}
                </div>
                <p className="text-sm text-muted-foreground mt-1">Trimestres restants</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* CSRD 2027 Timeline Alert */}
        {orgProgress && orgProgress.csrd_readiness < 100 && (
          <Card className="mb-8 border-chart-4">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-chart-4/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-chart-4" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1" style={{ fontFamily: 'Manrope' }}>
                    Objectif CSRD 2027
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Pour atteindre la conformité CSRD d'ici 2027, vous devez améliorer votre score ESG de{" "}
                    <span className="font-bold text-chart-4">
                      {orgProgress.timeline_to_2027.progress_needed_per_quarter}%
                    </span>{" "}
                    par trimestre.
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {orgProgress.timeline_to_2027.days_remaining} jours restants
                    </span>
                    <span className="text-muted-foreground">
                      <Target className="w-4 h-4 inline mr-1" />
                      Cible: 70% minimum
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for ESG Categories */}
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">Tous</TabsTrigger>
            <TabsTrigger value="environnement" data-testid="tab-env">
              <Leaf className="w-4 h-4 mr-2" />
              Environnement
            </TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social">
              <Users className="w-4 h-4 mr-2" />
              Social
            </TabsTrigger>
            <TabsTrigger value="gouvernance" data-testid="tab-gouv">
              <Building2 className="w-4 h-4 mr-2" />
              Gouvernance
            </TabsTrigger>
          </TabsList>

          {["all", "environnement", "social", "gouvernance"].map((tab) => (
            <TabsContent key={tab} value={tab}>
              <div className="grid gap-6">
                {objectives.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-medium text-foreground mb-2">Aucun objectif défini</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Créez votre premier objectif RSE pour suivre votre progression CSRD
                      </p>
                      <Button className="rounded-full" onClick={() => setDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Créer un objectif
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  objectives
                    .filter(obj => tab === "all" || obj.category === tab)
                    .map((objective) => (
                      <Card key={objective.id} className="card-hover" data-testid={`objective-${objective.id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              {getCategoryIcon(objective.category)}
                              <div>
                                <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                                  {objective.name}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {objective.quarter}
                                  </Badge>
                                  {objective.csrd_indicator && (
                                    <Badge variant="secondary" className="text-xs">
                                      {objective.csrd_indicator}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(objective.status)}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={() => handleDelete(objective.id)}
                                data-testid={`delete-objective-${objective.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {objective.description && (
                            <p className="text-sm text-muted-foreground mb-4">
                              {objective.description}
                            </p>
                          )}
                          
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Progression</span>
                              <span className="font-medium">
                                {objective.current_value}% / {objective.target_value}%
                              </span>
                            </div>
                            <Progress value={objective.progress_percentage} className="h-3" />
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>Valeur actuelle: {objective.current_value}%</span>
                              <span>{objective.progress_percentage}% atteint</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* ESG Targets Summary */}
        {orgProgress && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Écarts par rapport aux cibles CSRD</CardTitle>
              <CardDescription>
                Points d'amélioration nécessaires pour atteindre la conformité
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {Object.entries(orgProgress.esg_targets).map(([category, data]) => (
                  <div key={category} className="p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center gap-2 mb-3">
                      {getCategoryIcon(category)}
                      <span className="font-medium capitalize">{category}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Actuel</span>
                        <span className="font-bold">{data.current}%</span>
                      </div>
                      <Progress value={(data.current / data.target) * 100} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Cible: {data.target}%</span>
                        <span className={data.gap > 0 ? "text-chart-4" : "text-chart-1"}>
                          {data.gap > 0 ? `${data.gap}% à combler` : "Objectif atteint"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
